import React from 'react'

import {
  CompetitionMatch,
  MatchResult,
  Organizer,
  OrganizerLabels,
  formatDecision,
} from '../../../types/competition'

interface CompetitionCardProps {
  match: CompetitionMatch
  onEdit?: (match: CompetitionMatch) => void
  onDelete?: (matchId: string) => void
  showActions?: boolean
}

export default function CompetitionCard({
  match,
  onEdit,
  onDelete,
  showActions = false,
}: CompetitionCardProps) {
  const isWin = match.result === MatchResult.WON
  const isLoss = match.result === MatchResult.LOST

  const resultConfig = isWin
    ? {
        bg: 'bg-gradient-to-r from-emerald-500 via-emerald-500 to-green-500',
        tagBg: 'bg-emerald-700/90',
        label: 'VITÓRIA',
        icon: '🏆',
      }
    : isLoss
    ? {
        bg: 'bg-gradient-to-r from-rose-500 via-rose-500 to-red-500',
        tagBg: 'bg-rose-700/90',
        label: 'DERROTA',
        icon: '💔',
      }
    : {
        bg: 'bg-gradient-to-r from-slate-500 via-slate-500 to-gray-500',
        tagBg: 'bg-slate-700/90',
        label: 'EMPATE',
        icon: '🤝',
      }

  const formatDate = (date: string | Date) => {
    const d = typeof date === 'string' ? new Date(date) : date
    return d.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC'
    })
  }

  const getOrganizerLabel = (org: string) => {
    return OrganizerLabels[org as Organizer] || org
  }

  return (
    <div className="bg-white rounded-2xl shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] hover:shadow-[0_8px_30px_-4px_rgba(0,0,0,0.15)] transition-all duration-500 ease-out overflow-hidden group hover:-translate-y-1">
      {/* Result Banner - Responsive: stacks on mobile */}
      <div className={`${resultConfig.bg} px-4 py-3 sm:px-5 sm:py-4`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3">
            <span className="text-xl sm:text-2xl drop-shadow-sm">
              {resultConfig.icon}
            </span>
            <span className="font-black tracking-wider text-white text-xs sm:text-sm drop-shadow-sm">
              {resultConfig.label}
            </span>
          </div>
          <span
            className={`${resultConfig.tagBg} text-white text-[10px] sm:text-xs font-bold px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full shadow-sm self-start sm:self-auto whitespace-nowrap`}
          >
            {formatDecision(match)}
          </span>
        </div>
      </div>

      {/* Card Content */}
      <div className="p-4 sm:p-5">
        {/* Opponent Info */}
        <div className="mb-3 sm:mb-4">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h3 className="font-black text-gray-800 text-base sm:text-lg leading-tight tracking-tight">
              {match.opponent?.name ? `vs. ${match.opponent.name}` : ''}
            </h3>
            {showActions && (
              <div className="flex gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 transition-all duration-300 flex-shrink-0">
                {onEdit && (
                  <button
                    onClick={() => onEdit(match)}
                    className="p-1.5 sm:p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all duration-200"
                    title="Editar"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                    </svg>
                  </button>
                )}
                {onDelete && (
                  <button
                    onClick={() => onDelete(match.identificator.uuid)}
                    className="p-1.5 sm:p-2 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-all duration-200"
                    title="Excluir"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                )}
              </div>
            )}
          </div>
          {match.opponent?.academy && (
            <p className="text-gray-500 text-xs sm:text-sm font-medium">
              {match.opponent.academy}
            </p>
          )}
        </div>

        {/* Event Info */}
        <div className="bg-gradient-to-br from-gray-50 to-gray-100/80 rounded-xl p-3 sm:p-4 mb-3 sm:mb-4 border border-gray-100">
          <div className="flex items-start sm:items-center gap-2 mb-2">
            <div className="w-6 h-6 sm:w-7 sm:h-7 bg-white rounded-lg shadow-sm flex items-center justify-center flex-shrink-0">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
            <span className="text-gray-800 font-semibold text-xs sm:text-sm line-clamp-2">
              {match.eventName}
            </span>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 text-[10px] sm:text-xs pl-8 sm:pl-9">
            <span className="text-gray-500 font-medium">
              {formatDate(match.eventDate)}
            </span>
            {match.organizer && (
              <span className="bg-white text-gray-600 px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-lg font-semibold shadow-sm border border-gray-200">
                {getOrganizerLabel(match.organizer)}
              </span>
            )}
          </div>
        </div>

        {/* Category & Stats */}
        <div className="flex flex-wrap items-center justify-between gap-2 text-[10px] sm:text-xs pt-2 border-t border-gray-100">
          {match.category?.weight && (
            <span className="text-gray-500 font-medium">
              {match.category.weight}
              {match.category.age && (
                <span className="text-gray-400"> • {match.category.age}</span>
              )}
            </span>
          )}
          {match.statistics &&
            (match.statistics.points !== undefined ||
              match.statistics.advantages !== undefined) && (
              <span className="text-gray-600 font-semibold bg-gray-100 px-2 py-1 rounded-md">
                {match.statistics.points !== undefined && (
                  <>
                    {match.statistics.points}
                    {match.statistics.opponentPoints !== undefined
                      ? `-${match.statistics.opponentPoints}`
                      : ''}{' '}
                    pts
                  </>
                )}
                {match.statistics.advantages !== undefined && (
                  <>
                    {' '}
                    • {match.statistics.advantages}
                    {match.statistics.opponentAdvantages !== undefined
                      ? `-${match.statistics.opponentAdvantages}`
                      : ''}{' '}
                    vant
                  </>
                )}
              </span>
            )}
        </div>
      </div>
    </div>
  )
}
